import type {
  CreateClientRoleRequest,
  CreateClientUserRequest,
  CreateDeptRequest,
  CreateDictItemRequest,
  CreateDictTypeRequest,
  CreateMenuRequest,
  CreateModuleUserRequest,
  CreateParamRequest,
  CreateRoleRequest,
  CreateUserRequest,
  UpdateClientRoleRequest,
  UpdateClientUserRequest,
  UpdateDeptRequest,
  UpdateDictItemRequest,
  UpdateDictTypeRequest,
  UpdateMenuRequest,
  UpdateModuleUserRequest,
  UpdateModuleUserStatusRequest,
  UpdateParamRequest,
  UpdateRoleRequest,
  UpdateTenantOpenAppGrantRequest
} from '@xjlv2/api-client'
import type { MenuReorderItem } from '../types'
import type { useSaasWorkspace } from './useSaasWorkspace'

export interface UseRbacActionHandlersOptions {
  workspace: ReturnType<typeof useSaasWorkspace>
  setNotice: (message: string) => void
}

export function useRbacActionHandlers({ workspace, setNotice }: UseRbacActionHandlersOptions) {
  async function runAction(
    action: () => Promise<void>,
    successMessage: string,
    fallbackErrorMessage: string,
    onErrorFallback?: () => Promise<void>
  ) {
    setNotice('')
    try {
      await action()
      setNotice(successMessage)
    } catch (err) {
      if (onErrorFallback) {
        await onErrorFallback()
      }
      workspace.error.value = err instanceof Error ? err.message : fallbackErrorMessage
    }
  }

  return {
    createRole: (payload: CreateRoleRequest) =>
      runAction(
        () => workspace.createRole(payload),
        '角色已创建，权限立即生效到后续请求头。',
        '创建角色失败'
      ),
    updateRole: (roleId: number, payload: UpdateRoleRequest) =>
      runAction(
        () => workspace.updateRole(roleId, payload),
        '角色权限已保存，重新登录或刷新会话后权限集合生效。',
        '保存角色失败'
      ),
    createUser: (payload: CreateUserRequest) =>
      runAction(
        () => workspace.createUser(payload),
        '用户已创建，可用账号密码登录 SaaS 后台。',
        '创建用户失败'
      ),
    createClientRole: (payload: CreateClientRoleRequest) =>
      runAction(
        () => workspace.createClientRole(payload),
        'C 端角色已创建，菜单和 API 权限已写入客户端授权表。',
        '创建 C 端角色失败'
      ),
    updateClientRole: (roleId: number, payload: UpdateClientRoleRequest) =>
      runAction(
        () => workspace.updateClientRole(roleId, payload),
        'C 端角色授权已更新，客户端下次请求立即按新权限校验。',
        '保存 C 端角色失败'
      ),
    createClientUser: (payload: CreateClientUserRequest) =>
      runAction(
        () => workspace.createClientUser(payload),
        'C 端登录用户已创建，可通过 Client Gateway 登录。',
        '创建 C 端用户失败'
      ),
    updateClientUser: (userId: number, payload: UpdateClientUserRequest) =>
      runAction(
        () => workspace.updateClientUser(userId, payload),
        'C 端用户资料、状态和角色授权已保存。',
        '保存 C 端用户失败'
      ),
    createModuleUser: (payload: CreateModuleUserRequest) =>
      runAction(
        () => workspace.createModuleUser(payload),
        '模块用户已创建，可供该租户的对应模块调用。',
        '创建模块用户失败'
      ),
    updateModuleUser: (userId: number, payload: UpdateModuleUserRequest) =>
      runAction(
        () => workspace.updateModuleUser(userId, payload),
        '模块用户资料已保存。',
        '保存模块用户失败'
      ),
    updateModuleUserStatus: (userId: number, payload: UpdateModuleUserStatusRequest) =>
      runAction(
        () => workspace.updateModuleUserStatus(userId, payload),
        '模块用户状态已更新。',
        '更新模块用户状态失败'
      ),
    createDept: (payload: CreateDeptRequest) =>
      runAction(
        () => workspace.createDept(payload),
        '部门已创建，可用于用户归属和数据权限范围。',
        '创建部门失败'
      ),
    updateDept: (deptId: number, payload: UpdateDeptRequest) =>
      runAction(
        () => workspace.updateDept(deptId, payload),
        '部门配置已保存，数据权限范围会按最新组织结构计算。',
        '保存部门失败'
      ),
    createParam: (payload: CreateParamRequest) =>
      runAction(
        () => workspace.createParam(payload),
        '参数已创建。',
        '创建参数失败'
      ),
    updateParam: (code: string, payload: UpdateParamRequest) =>
      runAction(
        () => workspace.updateParam(code, payload),
        '参数已保存。',
        '保存参数失败'
      ),
    deleteParam: (code: string) =>
      runAction(
        () => workspace.deleteParam(code),
        '参数已删除。',
        '删除参数失败'
      ),
    createDict: (payload: CreateDictTypeRequest) =>
      runAction(
        () => workspace.createDict(payload),
        '字典已创建，可继续维护字典配置。',
        '创建字典失败'
      ),
    updateDict: (type: string, payload: UpdateDictTypeRequest) =>
      runAction(
        () => workspace.updateDict(type, payload),
        '字典已保存。',
        '保存字典失败'
      ),
    deleteDict: (type: string) =>
      runAction(
        () => workspace.deleteDict(type),
        '字典和下属字典项已删除。',
        '删除字典失败'
      ),
    createDictItem: (type: string, payload: CreateDictItemRequest) =>
      runAction(
        () => workspace.createDictItem(type, payload),
        '字典项已创建。',
        '创建字典项失败'
      ),
    updateDictItem: (type: string, value: string, payload: UpdateDictItemRequest) =>
      runAction(
        () => workspace.updateDictItem(type, value, payload),
        '字典项已保存。',
        '保存字典项失败'
      ),
    deleteDictItem: (type: string, value: string) =>
      runAction(
        () => workspace.deleteDictItem(type, value),
        '字典项已删除。',
        '删除字典项失败'
      ),
    reorderDictItems: (type: string, values: string[]) =>
      runAction(
        () => workspace.reorderDictItems(type, values),
        '字典项排序已保存。',
        '保存字典项排序失败',
        () => workspace.refreshWorkspace()
      ),
    createMenu: (payload: CreateMenuRequest) =>
      runAction(
        () => workspace.createMenu(payload),
        '菜单已创建，可继续维护下级菜单和授权标识。',
        '创建菜单失败'
      ),
    updateMenu: (menuCode: string, payload: UpdateMenuRequest) =>
      runAction(
        () => workspace.updateMenu(menuCode, payload),
        '菜单配置已保存，工作台菜单已按租户配置刷新。',
        '保存菜单失败'
      ),
    reorderMenus: (updates: MenuReorderItem[]) =>
      runAction(
        () => workspace.reorderMenus(updates),
        '菜单排序已保存，工作台菜单已按新的顺序刷新。',
        '保存菜单排序失败'
      ),
    deleteMenu: (menuCode: string) =>
      runAction(
        () => workspace.deleteMenu(menuCode),
        '菜单已删除，下级菜单和角色菜单授权已同步清理。',
        '删除菜单失败'
      ),
    updateOpenAppGrant: (appId: number, payload: UpdateTenantOpenAppGrantRequest) =>
      runAction(
        () => workspace.updateOpenAppGrant(appId, payload),
        '开放应用授权已保存，外部调用会按新的 API 权限和数据范围执行。',
        '保存开放授权失败'
      )
  }
}
